// API utility functions
import { buildApiUrl } from '../config/api';

export interface ApiError {
  detail: string;
  status?: number;
}

export async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = localStorage.getItem('access_token');
  
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers,
  };

  try {
    // Use buildApiUrl helper to ensure correct URL construction
    const fullUrl = buildApiUrl(endpoint);
    
    const response = await fetch(fullUrl, {
      ...options,
      headers,
      credentials: 'include', // Include cookies for cross-origin requests
      mode: 'cors', // Explicitly set CORS mode
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      const error: ApiError = {
        detail: data.detail || `HTTP error! status: ${response.status}`,
        status: response.status,
      };
      
      // Only clear token on 401 if error message explicitly indicates token expiration
      // Don't clear on other errors (404, 500, HTML responses, etc.)
      if (response.status === 401) {
        const errorDetail = (data.detail || '').toLowerCase();
        // Only clear if it's explicitly a token expiration error (not just any mention of "token" or "authentication")
        // This prevents clearing token on generic auth errors that aren't about expiration
        if (errorDetail.includes('expired') || 
            errorDetail.includes('token expired') ||
            errorDetail.includes('jwt expired') ||
            (errorDetail.includes('invalid token') && errorDetail.includes('expired'))) {
          console.log('Token expired detected in api.ts, clearing token');
          localStorage.removeItem('access_token');
          localStorage.removeItem('user_id');
          localStorage.removeItem('user_role');
        } else {
          console.warn('401 error but not token expiration:', data.detail || 'Unknown error');
        }
      }
      
      throw error;
    }

    return data as T;
  } catch (error: any) {
    if (error.detail) {
      throw error;
    }
    const apiError: ApiError = {
      detail: error.message || 'Network error. Please check your connection.',
      status: 0,
    };
    throw apiError;
  }
}

export function getToken(): string | null {
  return localStorage.getItem('access_token');
}

export function isAuthenticated(): boolean {
  return !!getToken();
}

export function clearAuth(): void {
  localStorage.removeItem('access_token');
  localStorage.removeItem('user_id');
  localStorage.removeItem('user_role');
}

/**
 * Safely extract error message from API response
 * Handles FastAPI validation errors which can be arrays of objects
 */
export function getErrorMessage(error: any, defaultMessage: string = 'An error occurred'): string {
  if (!error) return defaultMessage;
  
  // If it's already a string, return it
  if (typeof error === 'string') return error;
  
  // If it has a detail property
  if (error.detail) {
    if (typeof error.detail === 'string') {
      return error.detail;
    }
    if (Array.isArray(error.detail)) {
      // FastAPI validation errors are arrays
      return error.detail.map((err: any) => {
        if (typeof err === 'string') return err;
        if (err.msg) return err.msg;
        if (err.loc && err.msg) return `${err.loc.join('.')}: ${err.msg}`;
        return JSON.stringify(err);
      }).join(', ');
    }
    return String(error.detail);
  }
  
  // If it has a message property
  if (error.message) {
    return typeof error.message === 'string' ? error.message : String(error.message);
  }
  
  // If it's an array, try to extract messages
  if (Array.isArray(error)) {
    return error.map((err: any) => {
      if (typeof err === 'string') return err;
      if (err.msg) return err.msg;
      return JSON.stringify(err);
    }).join(', ');
  }
  
  // Last resort: stringify the error
  try {
    return JSON.stringify(error);
  } catch {
    return defaultMessage;
  }
}


